import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useWholesaleAuth } from '../contexts/WholesaleAuthContext';
import styles from '../styles/WholesaleLogin.module.css';

const WholesaleLogin = () => {
  const [email, setEmail] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { wholesaleLogin } = useWholesaleAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Add class to body to remove global gradient
    document.body.classList.add('wholesale-body');
    document.body.style.padding = '0';
    document.body.style.margin = '0';

    // Add viewport meta tag for mobile support
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.head.appendChild(viewportMeta);
    } else {
      viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
    }

    return () => {
      // Cleanup
      document.body.classList.remove('wholesale-body');
      document.body.style.padding = '';
      document.body.style.margin = '';
      
      // Reset viewport meta tag
      if (viewportMeta) {
        viewportMeta.content = 'width=device-width, initial-scale=1.0';
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await wholesaleLogin(email, accountNumber, password);
      navigate('/wholesale-dashboard');
    } catch (error) {
      console.error('Login error:', error);
      if (error.message.includes('Invalid email, account number')) {
        setError('Invalid email, account number, or account not approved');
      } else if (error.message.includes('Invalid credentials')) {
        setError('Invalid password');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.loginCard}>
        <div className={styles.header}>
          <img src="/images/logo.png" alt="Grey Official Logo" className={styles.logo} />
          <h1 className={styles.title}>Wholesale Portal</h1>
          <p className={styles.subtitle}>Access your wholesale account</p>
        </div>

        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
              placeholder="Enter your email address"
              required
              disabled={loading}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="accountNumber" className={styles.label}>
              Account Number
            </label>
            <input
              type="text"
              id="accountNumber"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              className={styles.input}
              placeholder="Enter your account number"
              required
              disabled={loading}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              placeholder="Enter your password"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className={styles.footer}>
          <p className={styles.footerText}>
            Don't have a wholesale account?{' '}
            <Link to="/wholesale-application" className={styles.link}>
              Apply here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default WholesaleLogin;